/**
 * The drawing layer, now owned by lightweight-charts-drawing.
 *
 * This replaced a hand-built SVG overlay. The library brings TradingView's
 * own 86 tools with their factory defaults, hit tests, placement rules and
 * gestures -- press-drag, double-click to finish a path, anchor and body
 * drags, Ctrl-click multi-select, Shift for 45-degree steps, Escape and
 * Delete. All of that was either missing or mine to maintain before.
 *
 * What stays ours is what the library leaves to the host: storage, the
 * toolbar, and the text editor.
 */
import { type Bar } from "@tt/shared";
import { DrawingManager, type DrawingKind } from "lightweight-charts-drawing";
import type { IChartApi, ISeriesApi, SeriesType } from "lightweight-charts";
import { useEffect, useRef, useState } from "react";

/** Drawings belong to a symbol — an AAPL trendline is meaningless on NVDA.
 *
 *  When `scope` is given (a workspace tile id, say), the storage is further
 *  partitioned so two tiles on the same symbol keep their own trendlines.
 *  Single-chart view passes no scope and keeps its original key.
 */
const keyFor = (symbol: string, scope?: string): string =>
  scope ? `tt.draw.${scope}.${symbol}` : `tt.draw.${symbol}`;

export interface DrawingsProps {
  chart: IChartApi | null;
  series: ISeriesApi<SeriesType> | null;
  bars: Bar[];
  symbol: string;
  /** The armed tool, or null for the cursor. */
  tool: DrawingKind | null;
  magnet: boolean;
  /** TradingView's "Keep drawing": stay armed after placing one. */
  stayInDrawingMode: boolean;
  /** The library disarms itself after a placement; the toolbar follows. */
  onToolChange: (kind: DrawingKind | null) => void;
  onManager?: (m: DrawingManager | null) => void;
  /** Extra storage partition, so e.g. workspace tiles on the same symbol do
   *  not share one set of drawings. Single-chart view omits this. */
  scope?: string;
}

export function Drawings({
  chart, series, bars, symbol, tool, magnet, stayInDrawingMode,
  onToolChange, onManager, scope,
}: DrawingsProps) {
  const mgr = useRef<DrawingManager | null>(null);
  const barsRef = useRef(bars);
  barsRef.current = bars;
  const [editing, setEditing] = useState<{ id: string; x: number; y: number; text: string } | null>(null);
  /**
   * False until the saved drawings are back in the manager.
   *
   * importJSON settles over a frame, and the manager emits a change with an
   * empty list on the way -- which, saved, overwrote the file being restored
   * with "[]". The restore then raced its own wipe: it won here, and would
   * not have on a slower frame or a failed import. Nothing is written until
   * what is on disk has been read.
   */
  const hydrated = useRef(false);

  // Callbacks reach the manager through a ref so the subscription is made
  // once per chart. Re-subscribing on every render would rebuild the
  // primitive mid-gesture.
  const cb = useRef({ onToolChange });
  cb.current = { onToolChange };

  useEffect(() => {
    if (!chart || !series) return;
    const m = new DrawingManager(chart, series, {
      // A getter, not a snapshot: the manager reads bars whenever it needs
      // them, so magnet and the volume tools follow a symbol or range change
      // without the manager being rebuilt.
      bars: () => barsRef.current as never,
      magnet: magnet ? "weak" : "off",
      stayInDrawingMode,
    });
    mgr.current = m;
    onManager?.(m);
    // A handle for the console in development. The drawing state lives inside
    // the library, so without this there is no way to ask it what it holds.
    if (import.meta.env.DEV) {
      (window as unknown as { __drawings?: DrawingManager }).__drawings = m;
    }

    hydrated.current = false;
    const saved = localStorage.getItem(keyFor(symbol, scope));
    if (saved) {
      try {
        m.importJSON(saved);
      } catch {
        // A drawing file from an older format is dropped rather than left to
        // throw on every render. Losing drawings is bad; a chart that will
        // not mount is worse.
        localStorage.removeItem(keyFor(symbol, scope));
      }
    }

    const offs = [
      m.on("change", () => {
        if (!hydrated.current) return;
        localStorage.setItem(keyFor(symbol, scope), m.exportJSON());
      }),
      m.on("tool", (kind) => cb.current.onToolChange(kind)),
      // The library draws every tool but leaves text entry to the host, which
      // is the one piece of the old layer worth keeping.
      m.on("textEdit", (d, screen) => {
        setEditing({ id: d.id, x: screen.x, y: screen.y, text: (d as { text?: string }).text ?? "" });
      }),
    ];
    // After the import has settled. requestAnimationFrame rather than a
    // timeout: it is the same frame boundary the manager redraws on.
    const hydrate = requestAnimationFrame(() => { hydrated.current = true; });

    return () => {
      cancelAnimationFrame(hydrate);
      for (const off of offs) off();
      m.destroy();
      mgr.current = null;
      onManager?.(null);
    };
    // Rebuilt only when the chart itself is, or the symbol/scope changes:
    // those decide which saved drawings belong on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, series, symbol, scope]);

  useEffect(() => { mgr.current?.setTool(tool); }, [tool]);
  useEffect(() => { mgr.current?.setMagnet(magnet ? "weak" : "off"); }, [magnet]);
  useEffect(() => { mgr.current?.setStayInDrawingMode(stayInDrawingMode); }, [stayInDrawingMode]);

  if (!editing) return null;
  const close = (keep: boolean) => {
    const m = mgr.current;
    if (m && keep) {
      const d = m.get(editing.id);
      if (d) m.update({ ...d, text: editing.text.slice(0, 200) } as never);
    }
    setEditing(null);
  };
  return (
    <input
      className="draw-text-input"
      autoFocus
      style={{ left: editing.x + 6, top: editing.y - 11, pointerEvents: "auto" }}
      value={editing.text}
      placeholder="Add text"
      onChange={(e) => setEditing({ ...editing, text: e.target.value })}
      onKeyDown={(e) => {
        if (e.key === "Enter") close(true);
        if (e.key === "Escape") close(false);
        e.stopPropagation();
      }}
      onBlur={() => close(true)}
    />
  );
}
